// ZM - Reviewed by Claude Portal-Review Deep Dive 2026-05-05
import React from 'react';
import { Dialog } from '@framework-controls/core';
import classNames from 'classnames';
import { THEME } from 'src/constants/ThemeNames';
import { IconRemove, IconRemoveAudi } from 'src/content/icons';
import VinLocation from 'src/content/images/VinLocation.jpg';
import SVPButton from '../SVPButtons/SVPButton';
import { strings } from 'src/localization';
import css from './WhereIsMyVin.module.scss';

export interface IProps {
    whereHandler: (e: React.MouseEvent<HTMLElement | SVGSVGElement> | React.KeyboardEvent<HTMLElement>) => void;
    whereOpen: boolean;
    theme?: string;
}

const ANCHOR_ORIGIN = { vertical: 'top' } as const;

/** Modal dialog showing where to find the VIN on a vehicle. Controlled externally via whereOpen/whereHandler. */
const WhereIsMyVin = (props: IProps) => {
    const { theme, whereOpen, whereHandler } = props;
    const isAudi = theme === THEME.AUDI;
    // Audi only: the 40px wrapper owns click, keyboard (Enter/Space) and the accessible name; other themes keep the original icon.
    const closeKeyHandler = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            whereHandler(e);
        }
    };

    const headerBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxLetterSpaced]: theme === THEME.MAZDA || theme === THEME.GENESIS,
            [css.headerboxAudi]: isAudi,
        },
    );

    // Built per render rather than hoisted to a module constant, since the Alpha panel classes
    // depend on the theme.
    const dialogCssClasses = {
        content: classNames(css.content, { [css.contentAudi]: isAudi }),
        paper: classNames(css.paper, { [css.paperAudi]: isAudi }),
        header: css.LightBoxHeader,
    };

    const dialog = <Dialog
        cssClasses={dialogCssClasses}
        isOpen={whereOpen}
        anchorOrigin={ANCHOR_ORIGIN}
        header={strings.WhereVinHeader}
        hideSave={true}
        hideCancel={true}
        disableUpperCase={true}
        onCancel={whereHandler}
        onBackDropClick={whereHandler}
        width={'full'}
    >
        <div className={headerBoxStyles}>
            {strings.WhereVinHeader}
            {isAudi ?
                <div className={classNames(css.headerIcon, css.headerIconAudi)}
                    onClick={whereHandler}
                    onKeyDown={closeKeyHandler}
                    role='button'
                    tabIndex={0}
                    aria-label={strings.Close}>
                    <IconRemoveAudi id='WhereIsMyVin-closeBtn'
                        data-testid='WhereIsMyVin-closeBtn'/>
                </div> :
                <div className={css.headerIcon}>
                    <IconRemove id='WhereIsMyVin-closeBtn'
                        data-testid='WhereIsMyVin-closeBtn'
                        aria-label={strings.Close}
                        onClick={whereHandler}/>
                </div>}
        </div>
        <div>
            <div>
                <div className={css.typicalVin}>{strings.TypicalVINLoc}</div>
                <img
                    src={VinLocation}
                    alt={''}
                    className={css.VinLocation}
                />
            </div>
            <div className={css.otherVinLoc}>{strings.OtherVinLoc}</div>
            <ul className={css.otherVinLoc}>
                <li>{strings.InsuranceIdCard}</li>
                <li>{strings.VehRegCard}</li>
            </ul>
        </div>
        <div className={css.btnArea}>
            <SVPButton
                id='WhereIsMyVin-cancel'
                data-testid='WhereIsMyVin-cancel'
                handler={whereHandler}
                message={strings.Close}
                variant={'primaryBtnAlt'}
                noArrow
                theme={theme}/>
        </div>
    </Dialog>;

    return (
        <div>
            {dialog}
        </div>
    );
};

export default WhereIsMyVin;